import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  ButtonBase,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import type { Theme } from '@mui/material';
import { ChevronDown, ChevronsUpDown, Eye, LogOut, Menu as MenuIcon, PanelLeftClose, PanelLeftOpen, Search, Settings, X } from 'lucide-react';
import Sidebar, { getNavigationContext } from './Sidebar';
import type { NavigationContext } from './Sidebar';
import Notifications from './Notifications';
import ModalManager from './ModalManager';
import CommandPalette, { isMacPlatform, PaletteShortcut } from './CommandPalette';
import { renderThemeModeItems, useThemeMode } from './ThemeModeMenu';
import { PersonAvatar } from '../ui';
import { useAuth } from '../../contexts/AuthContext';
import { AuthRole, formatAuthRole } from '../../utils/roles';
import { focusRing, sbg, transition, typeStyle } from '../../theme/sbgTokens';
import logoFull from '../../assets/brand/sbg-logo-full-digital.svg';
import logoFullOnDark from '../../assets/brand/sbg-logo-full-color-white.svg';
import logoIcon from '../../assets/brand/sbg-icon-digital.svg';
import logoIconOnDark from '../../assets/brand/sbg-icon-color-white.svg';

/*
 * The app shell (book §8.5, §11.7): a 240px white sidebar (64px collapsed)
 * whose 56px logo row lines up with the 56px top bar, and a fluid Cloud
 * content area ("data pages run fluid inside the shell", §8.4) whose left
 * edge matches the breadcrumb's. DOM order is skip link → top bar →
 * navigation → main, so keyboard users meet search and their account first.
 */

const MOBILE_DRAWER_WIDTH = 280;
const SEARCH_LABEL = 'Search or jump to';
const COLLAPSE_PREF_KEY = 'sbg-attendance.sidebar-collapsed';

const readCollapsedPref = () => {
  try {
    return window.localStorage.getItem(COLLAPSE_PREF_KEY) === 'true';
  } catch {
    return false;
  }
};

const writeCollapsedPref = (collapsed: boolean) => {
  try {
    window.localStorage.setItem(COLLAPSE_PREF_KEY, String(collapsed));
  } catch {
    // Private windows and blocked storage: the sidebar still works, it just won't remember.
  }
};

const isFloorPlan = (pathname: string) => pathname.startsWith('/admin/floorplan');

/** Grid column, header padding and logo width animate together. */
const railMotion = (...properties: string[]) =>
  properties.map((property) => `${property} ${sbg.duration.base} ${sbg.ease.standard}`).join(', ');

/** What a record under a destination is called in the breadcrumb (/employees/:id). */
const RECORD_LABELS: Record<string, string> = {
  '/employees': 'Employee profile',
  '/departments': 'Department details',
  '/time-off': 'Request details',
};
const recordLabelFor = (context: NavigationContext) => (context.item && RECORD_LABELS[context.item.path]) || 'Details';

/* ------------------------------------------------------------------------ */
/* Logo: the digital lockup on light surfaces, color-white on dark (§5.2)    */
/* ------------------------------------------------------------------------ */

const BrandLogo: React.FC<{ variant: 'full' | 'icon' }> = ({ variant }) => {
  // Full lockup 32px tall, icon 24px (§5.3); explicit widths reserve the space before the SVG loads.
  const size = variant === 'full' ? { height: 32, width: 107 } : { height: 24, width: 33 };
  return (
    <>
      <Box
        component="img"
        src={variant === 'full' ? logoFull : logoIcon}
        alt="SBG Funding"
        sx={[{ ...size, display: 'block', flex: 'none' }, (theme) => theme.applyStyles('dark', { display: 'none' })]}
      />
      <Box
        component="img"
        src={variant === 'full' ? logoFullOnDark : logoIconOnDark}
        alt="SBG Funding"
        sx={[{ ...size, display: 'none', flex: 'none' }, (theme) => theme.applyStyles('dark', { display: 'block' })]}
      />
    </>
  );
};

/** Full lockup expanded, icon collapsed: a crossfade inside a width that animates with the rail. */
const SidebarLogo: React.FC<{ collapsed: boolean }> = ({ collapsed }) => (
  <Box
    sx={{
      position: 'relative',
      flex: 'none',
      height: 32,
      width: collapsed ? 33 : 107,
      overflow: 'hidden',
      transition: railMotion('width'),
    }}
  >
    <Box aria-hidden={collapsed} sx={{ position: 'absolute', left: 0, top: 0, opacity: collapsed ? 0 : 1, transition: transition('opacity') }}>
      <BrandLogo variant="full" />
    </Box>
    <Box aria-hidden={!collapsed} sx={{ position: 'absolute', left: 0, top: 4, opacity: collapsed ? 1 : 0, transition: transition('opacity') }}>
      <BrandLogo variant="icon" />
    </Box>
  </Box>
);

/*
 * Logo row: 56px including its hairline, so it meets the top bar's hairline
 * exactly. Expanded, the lockup sits at 24px (the nav icons' edge) and
 * "Attendance" fits in full: 107 + 8 + 1 + 8 + 72px label = 196px of the
 * 199px between 24px and 16px padding. Collapsed, the 33px icon sits at 16px,
 * centered in the 64px rail.
 */
const SidebarHeader: React.FC<{ collapsed: boolean; onClose?: () => void; drawerOpen: boolean }> = ({ collapsed, onClose, drawerOpen }) => (
  <Box
    sx={{
      height: sbg.layout.topbarH,
      minHeight: sbg.layout.topbarH,
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      pl: collapsed ? 2 : 3,
      pr: onClose ? 1.5 : 2,
      borderBottom: `1px solid ${sbg.border.default}`,
      flex: 'none',
      transition: railMotion('padding-left'),
    }}
  >
    <Box
      component={RouterLink}
      to="/"
      aria-label="Attendance home"
      sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        borderRadius: sbg.radius.sm,
        textDecoration: 'none',
        '&:focus-visible': focusRing,
      }}
    >
      <SidebarLogo collapsed={collapsed} />
      <Box
        aria-hidden="true"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          // Padding collapses too, so the collapsed link (and its focus ring) is just the icon.
          pl: collapsed ? 0 : 1,
          minWidth: 0,
          maxWidth: collapsed ? 0 : 200,
          overflow: 'hidden',
          opacity: collapsed ? 0 : 1,
          transition: railMotion('max-width', 'padding-left', 'opacity'),
        }}
      >
        <Box sx={{ width: '1px', height: 20, bgcolor: sbg.border.default, flex: 'none' }} />
        <Typography variant="labelSm" component="span" noWrap sx={{ color: sbg.text.secondary }}>
          Attendance
        </Typography>
      </Box>
    </Box>
    {onClose && (
      <Tooltip title="Close navigation">
        <IconButton
          aria-label="Close navigation"
          aria-controls="primary-navigation"
          aria-expanded={drawerOpen}
          onClick={onClose}
          size="small"
          sx={{ ml: 'auto', flex: 'none' }}
        >
          <X />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

const CollapseToggle: React.FC<{ collapsed: boolean; expanded: boolean; onToggle: () => void }> = ({ collapsed, expanded, onToggle }) => {
  const label = collapsed ? 'Expand navigation' : 'Collapse navigation';
  return (
    <Box sx={{ px: 1.5, pt: 0.5, pb: 1.5, flex: 'none' }}>
      <Tooltip title={collapsed ? label : ''} placement="right">
        <ButtonBase
          aria-label={label}
          aria-controls="primary-navigation"
          aria-expanded={expanded}
          onClick={onToggle}
          sx={{
            width: '100%',
            height: 36,
            gap: '10px',
            // Same padding in both states: the icon stays put while the rail animates.
            px: 1.5,
            justifyContent: 'flex-start',
            overflow: 'hidden',
            borderRadius: sbg.radius.pill,
            color: sbg.text.secondary,
            ...typeStyle('label-sm'),
            transition: transition('background-color', 'color'),
            '&:hover': { bgcolor: sbg.bg.hover, color: sbg.text.primary },
            '&.Mui-focusVisible': focusRing,
          }}
        >
          {collapsed ? <PanelLeftOpen size={18} aria-hidden="true" /> : <PanelLeftClose size={18} aria-hidden="true" />}
          <Box
            component="span"
            aria-hidden="true"
            sx={{ whiteSpace: 'nowrap', opacity: collapsed ? 0 : 1, transition: transition('opacity') }}
          >
            Collapse
          </Box>
        </ButtonBase>
      </Tooltip>
    </Box>
  );
};

/* ------------------------------------------------------------------------ */
/* Top bar pieces                                                            */
/* ------------------------------------------------------------------------ */

/*
 * Top bar context (book §11.6). Top-level pages show just the page name (the
 * page header repeats it; the sidebar group is not a hierarchy level). A
 * record under a destination gets a real breadcrumb — "Employees / Employee
 * profile" — whose parent links back when the role can open it.
 */
const TopBarContext: React.FC<{ context: NavigationContext; compact: boolean; canOpenParent: boolean }> = ({
  context,
  compact,
  canOpenParent,
}) => {
  const current = context.nested ? recordLabelFor(context) : context.title;

  if (compact) {
    return (
      <Typography variant="labelMd" component="p" noWrap sx={{ color: sbg.text.primary, minWidth: 0 }}>
        {current}
      </Typography>
    );
  }

  if (!context.nested || !context.item) {
    return (
      <Typography component="p" noWrap sx={{ ...typeStyle('body-xs'), color: sbg.text.primary, minWidth: 0 }}>
        {current}
      </Typography>
    );
  }

  return (
    <Box component="nav" aria-label="Breadcrumb" sx={{ minWidth: 0 }}>
      <Box
        component="ol"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, m: 0, p: 0, listStyle: 'none', minWidth: 0, ...typeStyle('body-xs') }}
      >
        <Box component="li" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 'none', color: sbg.text.secondary }}>
          {canOpenParent ? (
            <Box
              component={RouterLink}
              to={context.item.path}
              sx={{
                color: 'inherit',
                textDecoration: 'none',
                borderRadius: sbg.radius.xs,
                transition: transition('color'),
                '&:hover': { color: sbg.text.primary, textDecoration: 'underline' },
                '&:focus-visible': focusRing,
              }}
            >
              {context.title}
            </Box>
          ) : (
            <span>{context.title}</span>
          )}
          <Box component="span" aria-hidden="true" sx={{ color: sbg.palette.slate[300] }}>
            /
          </Box>
        </Box>
        <Box
          component="li"
          aria-current="page"
          sx={{ minWidth: 0, color: sbg.text.primary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {current}
        </Box>
      </Box>
    </Box>
  );
};

const SearchTrigger: React.FC<{ onOpen: () => void; open: boolean }> = ({ onOpen, open }) => (
  <ButtonBase
    onClick={onOpen}
    aria-label={SEARCH_LABEL}
    aria-haspopup="dialog"
    aria-expanded={open}
    aria-keyshortcuts={isMacPlatform() ? 'Meta+K' : 'Control+K'}
    sx={{
      width: { md: 240, lg: 280 },
      height: 36,
      px: 1.5,
      gap: 1,
      justifyContent: 'flex-start',
      borderRadius: sbg.radius.pill,
      border: `1px solid ${sbg.border.default}`,
      bgcolor: sbg.bg.surface,
      color: sbg.text.tertiary,
      transition: transition('border-color', 'background-color'),
      '&:hover': { borderColor: sbg.border.hover, bgcolor: sbg.bg.hover },
      '&.Mui-focusVisible': focusRing,
    }}
  >
    <Search size={16} aria-hidden="true" />
    <Box component="span" sx={{ flex: 1, minWidth: 0, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', ...typeStyle('body-sm') }}>
      Search or jump to…
    </Box>
    {/* Below 1024px the hint would squeeze the label to "Search or jum…". */}
    <Box component="span" sx={{ display: { xs: 'none', lg: 'inline-flex' }, flex: 'none' }}>
      <PaletteShortcut />
    </Box>
  </ButtonBase>
);

interface AccountMenuProps {
  /** `topbar`: avatar, name and role (name from 1024px). `drawer`: a full-width row in the mobile drawer footer. */
  placement: 'topbar' | 'drawer';
}

const AccountMenu: React.FC<AccountMenuProps> = ({ placement }) => {
  const inDrawer = placement === 'drawer';
  const { profile, signOut, user, viewAsRole, actualRole } = useAuth();
  const { mode, setMode } = useThemeMode();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const canManageSettings = profile?.role === 'admin' || profile?.role === 'hr';
  const displayName = profile?.full_name?.trim() || user?.email?.split('@')[0]
    .split('.').filter(Boolean).map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ') || 'User';
  const roleLine = viewAsRole ? `${formatAuthRole(viewAsRole)} (preview)` : formatAuthRole(profile?.role);

  const handleMenuClose = () => setAnchorEl(null);
  const handleSignOut = () => {
    handleMenuClose();
    signOut();
  };

  return (
    <>
      <ButtonBase
        id="account-menu-button"
        aria-label={`Account menu for ${user?.email || 'user'}`}
        aria-haspopup="menu"
        aria-controls={anchorEl ? 'account-menu' : undefined}
        aria-expanded={Boolean(anchorEl)}
        onClick={event => setAnchorEl(event.currentTarget)}
        sx={{
          gap: 1,
          minWidth: 0,
          maxWidth: '100%',
          width: inDrawer ? '100%' : undefined,
          minHeight: inDrawer ? 44 : undefined,
          justifyContent: 'flex-start',
          p: inDrawer ? '6px 12px 6px 6px' : '4px 10px 4px 4px',
          borderRadius: sbg.radius.pill,
          textAlign: 'left',
          transition: transition('background-color'),
          '&:hover': { bgcolor: sbg.bg.hover },
          '&.Mui-focusVisible': focusRing,
        }}
      >
        <PersonAvatar name={displayName} size={32} />
        <Box
          component="span"
          sx={{ display: inDrawer ? 'block' : { xs: 'none', lg: 'block' }, flex: inDrawer ? 1 : undefined, maxWidth: inDrawer ? 'none' : 168, minWidth: 0 }}
        >
          <Typography variant="labelSm" component="span" noWrap sx={{ display: 'block', color: sbg.text.primary }}>
            {displayName}
          </Typography>
          <Typography variant="caption" component="span" noWrap sx={{ display: 'block', color: sbg.text.secondary }}>
            {roleLine}
          </Typography>
        </Box>
        <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', flex: 'none', color: sbg.text.secondary }}>
          {inDrawer ? <ChevronsUpDown size={16} /> : <ChevronDown size={16} />}
        </Box>
      </ButtonBase>
      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        transformOrigin={inDrawer ? { horizontal: 'left', vertical: 'bottom' } : { horizontal: 'right', vertical: 'top' }}
        anchorOrigin={inDrawer ? { horizontal: 'left', vertical: 'top' } : { horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          list: { 'aria-labelledby': 'account-menu-button' },
          paper: { sx: { minWidth: 264, ...(inDrawer ? { mt: -0.75 } : { mt: 0.75 }) } },
        }}
      >
        <ListSubheader
          role="none"
          disableSticky
          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '8px 8px 12px', textTransform: 'none', letterSpacing: 0, font: 'inherit' }}
        >
          <PersonAvatar name={displayName} size={40} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="labelMd" component="div" noWrap sx={{ color: sbg.text.primary }}>
              {displayName}
            </Typography>
            {user?.email && (
              <Typography variant="body2" component="div" noWrap sx={{ color: sbg.text.secondary }}>
                {user.email}
              </Typography>
            )}
            <Typography variant="caption" component="div" sx={{ color: sbg.text.secondary }}>
              {formatAuthRole(actualRole || profile?.role)} account
            </Typography>
          </Box>
        </ListSubheader>
        <Divider />
        {canManageSettings && [
          <MenuItem key="settings" component={RouterLink} to="/admin">
            <ListItemIcon>
              <Settings aria-hidden="true" />
            </ListItemIcon>
            Settings
          </MenuItem>,
          <Divider key="settings-divider" />,
        ]}
        {renderThemeModeItems(mode, setMode)}
        <Divider />
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <LogOut aria-hidden="true" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
};

/** Role preview strip under the top bar (§11.13 banner, info tokens). */
const PreviewBanner: React.FC<{ role: string; onExit: () => void }> = ({ role, onExit }) => (
  <Box
    sx={{
      gridArea: 'banner',
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      minHeight: 44,
      py: 0.75,
      px: { xs: 2, sm: 3 },
      bgcolor: sbg.status.info.bg,
      borderBottom: `1px solid ${sbg.status.info.border}`,
      color: sbg.status.info.text,
    }}
  >
    <Box component="span" aria-hidden="true" sx={{ display: 'inline-flex', color: sbg.status.info.icon, flex: 'none' }}>
      <Eye size={18} />
    </Box>
    <Typography role="status" variant="body2" component="p" sx={{ flex: 1, minWidth: 0, color: 'inherit' }}>
      <Box component="span" sx={{ ...typeStyle('label-sm') }}>Previewing as {role}.</Box>{' '}
      <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
        Navigation and pages show what this role can see. Your admin access is unchanged.
      </Box>
    </Typography>
    <Button size="small" variant="text" onClick={onExit} sx={{ flex: 'none' }}>
      Exit role preview
    </Button>
  </Box>
);

const SkipLink: React.FC<{ onSkip: () => void }> = ({ onSkip }) => (
  <Box
    component="a"
    href="#main-content"
    onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      onSkip();
    }}
    sx={{
      position: 'fixed',
      top: 8,
      left: 8,
      zIndex: (theme: Theme) => theme.zIndex.tooltip + 1,
      transform: 'translateY(-200%)',
      px: 2,
      py: 1,
      borderRadius: sbg.radius.pill,
      bgcolor: sbg.action.primary,
      color: sbg.text.onBrand,
      textDecoration: 'none',
      ...typeStyle('label-sm'),
      '&:focus': { transform: 'translateY(0)' },
      '&:focus-visible': focusRing,
    }}
  >
    Skip to main content
  </Box>
);

/* ------------------------------------------------------------------------ */
/* Layout                                                                    */
/* ------------------------------------------------------------------------ */

const Layout: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const location = useLocation();
  const collapsedPref = useRef(readCollapsedPref());
  const [open, setOpen] = useState(() => !isMobile && !isFloorPlan(location.pathname) && !collapsedPref.current);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const { profile, viewAsRole, setViewAsRole, actualRole } = useAuth();
  const collapsed = !isMobile && !open;
  const railWidth = collapsed ? sbg.layout.sidebarWCollapsed : sbg.layout.sidebarW;
  const context = getNavigationContext(location.pathname);
  const role = (profile?.role || 'employee') as AuthRole;
  // Managers can open /employees/:id but not the Employees list, so the parent is only a link when it would open.
  const canOpenParent = Boolean(context.item?.roles.includes(role));
  const previewing = Boolean(viewAsRole && actualRole === 'admin');

  // The floor plan opens with the sidebar collapsed for canvas room; elsewhere
  // the desktop sidebar follows the person's last choice. Mobile always closes.
  useEffect(() => {
    setOpen(!isMobile && !isFloorPlan(location.pathname) && !collapsedPref.current);
  }, [isMobile, location.pathname]);

  const toggleCollapsed = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (!isFloorPlan(location.pathname)) {
      collapsedPref.current = !nextOpen;
      writeCollapsedPref(!nextOpen);
    }
  };

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const closeDrawer = useCallback(() => setOpen(false), []);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : `${railWidth} minmax(0, 1fr)`,
        gridTemplateRows: 'auto auto minmax(0, 1fr)',
        gridTemplateAreas: isMobile ? '"top" "banner" "main"' : '"nav top" "nav banner" "nav main"',
        height: '100vh',
        '@supports (height: 100dvh)': { height: '100dvh' },
        bgcolor: sbg.bg.canvas,
        transition: `grid-template-columns ${sbg.duration.base} ${sbg.ease.standard}`,
      }}
    >
      <SkipLink onSkip={() => mainRef.current?.focus()} />

      {/* 56px including the theme's 1px hairline, matching the sidebar's logo row. */}
      <AppBar position="static" sx={{ gridArea: 'top', zIndex: 'auto', height: sbg.layout.topbarH, boxSizing: 'border-box' }}>
        {isMobile ? (
          // Mobile keeps the menu button, the title and one action (§8.5); the account lives in the drawer.
          // Gutters match the content's (16px, 24px from 640px); edge buttons pull their icons onto that line.
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', gap: 1, px: { xs: 2, sm: 3 } }}>
            <Tooltip title="Open navigation">
              <IconButton
                edge="start"
                aria-label="Open navigation"
                aria-controls="primary-navigation"
                aria-expanded={open}
                onClick={() => setOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TopBarContext context={context} compact canOpenParent={canOpenParent} />
            </Box>
            <Tooltip title={SEARCH_LABEL}>
              <IconButton edge="end" aria-label={SEARCH_LABEL} aria-haspopup="dialog" aria-expanded={paletteOpen} onClick={openPalette}>
                <Search />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
              alignItems: 'center',
              gap: 2,
              px: 3,
            }}
          >
            <TopBarContext context={context} compact={false} canOpenParent={canOpenParent} />
            <SearchTrigger onOpen={openPalette} open={paletteOpen} />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
              <AccountMenu placement="topbar" />
            </Box>
          </Box>
        )}
      </AppBar>

      {previewing && <PreviewBanner role={formatAuthRole(viewAsRole)} onExit={() => setViewAsRole(null)} />}

      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={open}
        onClose={closeDrawer}
        ModalProps={{ keepMounted: true }}
        sx={
          isMobile
            ? { '& .MuiDrawer-paper': { width: MOBILE_DRAWER_WIDTH, maxWidth: 'calc(100vw - 48px)', overflowX: 'hidden' } }
            : {
                gridArea: 'nav',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                '& .MuiDrawer-paper': { position: 'relative', width: '100%', height: '100%', overflowX: 'hidden' },
              }
        }
      >
        <SidebarHeader collapsed={collapsed} onClose={isMobile ? closeDrawer : undefined} drawerOpen={open} />
        <Sidebar onItemClick={isMobile ? closeDrawer : undefined} collapsed={collapsed} />
        {isMobile ? (
          <Box sx={{ flex: 'none', p: 1.5, borderTop: `1px solid ${sbg.border.default}` }}>
            <AccountMenu placement="drawer" />
          </Box>
        ) : (
          <CollapseToggle collapsed={collapsed} expanded={open} onToggle={toggleCollapsed} />
        )}
      </Drawer>

      <Box
        component="main"
        id="main-content"
        ref={mainRef}
        tabIndex={-1}
        sx={{ gridArea: 'main', minWidth: 0, minHeight: 0, overflow: 'auto', scrollbarGutter: 'stable', '&:focus': { outline: 'none' } }}
      >
        {/* Fluid and left-aligned: the page header shares the breadcrumb's 24px edge at any width. */}
        <Box sx={{ width: '100%', p: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Box>
      </Box>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <Notifications />
      <ModalManager />
    </Box>
  );
};

export default Layout;
